import React, {useState, useMemo} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import {SeoHead} from "../app/SeoHead";
import {useNavigate} from "react-router";
import {useNews} from "../firebase/useNews";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {NewsCard} from "./NewsCard";

export const NewsPage = () => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();
    const [searchQuery, setSearchQuery] = useState("");

    const news = useNews({
        orderBy: "published",
        order: "desc",
    });

    const publishedArticles = useMemo(() => {
        if (!news) return [];
        return news.filter((art) => art.status !== "draft");
    }, [news]);

    const filteredNews = useMemo(() => {
        if (!searchQuery.trim()) return publishedArticles;
        const q = searchQuery.toLowerCase().trim();
        return publishedArticles.filter(
            (article) =>
                (article.title && article.title.toLowerCase().includes(q)) ||
                (article.text && article.text.toLowerCase().includes(q))
        );
    }, [publishedArticles, searchQuery]);

    return (
        <Box sx={{bgcolor: "#FAF8F5", minHeight: "85vh", py: {xs: 3, md: 5}}}>
            <SeoHead
                title="Aktuelt og nyheter"
                description="Siste nytt om kursstarter, faglige oppdateringer og arrangementer innen bekymringsmestring."
                path="/news"
            />

            <Container maxWidth="lg" sx={{px: {xs: 2, sm: 3}}}>
                {/* Navigation and Admin Bar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/#aktuelt")}
                        sx={{
                            color: "text.secondary",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            minHeight: 44,
                            "&:hover": {color: "primary.main"},
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Tilbake til forsiden
                    </Button>

                    {isAdmin && (
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<AdminPanelSettingsIcon />}
                            onClick={() => navigate("/admin/news")}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                textTransform: "none",
                                minHeight: 44,
                                "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                            }}
                        >
                            Administrer nyheter
                        </Button>
                    )}
                </Box>

                {/* Page Title */}
                <Box sx={{mb: 4}}>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            fontSize: {xs: "1.85rem", sm: "2.35rem", md: "2.75rem"},
                            letterSpacing: "-0.02em",
                            mb: 1,
                        }}
                    >
                        Aktuelt og nyheter
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{fontSize: {xs: "1rem", sm: "1.1rem"}, maxWidth: 720, lineHeight: 1.6}}
                    >
                        Følg med på siste nytt, oppstart av nye kursgrupper, faglige artikler og kunngjøringer fra nettverket for bekymringsmestring.
                    </Typography>
                </Box>

                {/* Search Bar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 4,
                        bgcolor: "background.paper",
                        p: {xs: 1.5, sm: 2},
                        borderRadius: 3,
                        border: "1px solid rgba(40, 82, 56, 0.12)",
                    }}
                >
                    <TextField
                        placeholder="Søk i nyheter og kunngjøringer..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        inputProps={{
                            "aria-label": "Søk i nyheter og kunngjøringer",
                        }}
                        sx={{
                            flex: 1,
                            minWidth: {xs: "100%", sm: 260},
                            "& .MuiOutlinedInput-root": {borderRadius: 2},
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{color: "text.secondary"}} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Tøm søk"
                                        size="small"
                                        onClick={() => setSearchQuery("")}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    <Chip
                        label={
                            searchQuery.trim()
                                ? `Viser ${filteredNews.length} av ${publishedArticles.length} artikler`
                                : `${publishedArticles.length} ${publishedArticles.length === 1 ? "artikkel" : "artikler"}`
                        }
                        size="medium"
                        role="status"
                        aria-live="polite"
                        sx={{
                            fontWeight: 600,
                            bgcolor: "rgba(40, 82, 56, 0.08)",
                            color: "primary.main",
                            borderRadius: 2,
                        }}
                    />
                </Box>

                {/* News Grid */}
                {filteredNews.length > 0 ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                            },
                            gap: 3,
                        }}
                    >
                        {filteredNews.map((article) => (
                            <NewsCard key={article.id} article={article} />
                        ))}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            textAlign: "center",
                            p: 6,
                            bgcolor: "background.paper",
                            borderRadius: 3,
                            border: "1px dashed rgba(40, 82, 56, 0.2)",
                        }}
                    >
                        <Typography variant="h6" color="text.primary" sx={{fontWeight: 700, mb: 1}}>
                            Ingen artikler funnet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2.5}}>
                            {searchQuery
                                ? `Ingen artikler matcher søket «${searchQuery}». Prøv et annet søkeord.`
                                : "Det er for øyeblikket ingen publiserte nyhetsartikler."}
                        </Typography>
                        {searchQuery && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setSearchQuery("")}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    minHeight: 44,
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: 2,
                                    },
                                }}
                            >
                                Nullstill søk
                            </Button>
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );
};
